import { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef } from 'react';
import { OrderItem } from '@/types/product';
import { useAuth } from '@/hooks/useAuth';
import { logger } from '@/services/logger';
import { logActivity, ACTION_TYPES } from '@/services/activityLogger';

export interface CartItem extends OrderItem {
  id: string;
}

export interface CartState {
  items: CartItem[];
  totalItems: number;
  totalAmount: number;
}

export interface SavedCart {
  id: string;
  name: string;
  userId: string;
  items: CartItem[];
  savedAt: string;
  totalAmount: number;
  itemCount: number;
}

interface CartContextType {
  cart: CartState;
  savedCarts: SavedCart[];
  currentCartName: string | null;
  currentCartId: string | null;
  isModified: boolean;
  lastSavedAt: string | null;
  autoSaveEnabled: boolean;
  wasAutoRestored: boolean;
  clearAutoRestoredFlag: () => void;
  addToCart: (items: OrderItem[]) => Promise<void>;
  removeFromCart: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  saveCart: (name: string) => void;
  loadCart: (cartId: string) => void;
  deleteSavedCart: (cartId: string) => void;
  startNewCart: () => void;
  enableAutoSave: () => void;
  disableAutoSave: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [cart, setCart] = useState<CartState>({
    items: [],
    totalItems: 0,
    totalAmount: 0
  });
  const [savedCarts, setSavedCarts] = useState<SavedCart[]>([]);
  const [currentCartName, setCurrentCartName] = useState<string | null>(null);
  const [currentCartId, setCurrentCartId] = useState<string | null>(null);
  const [isModified, setIsModified] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [wasAutoRestored, setWasAutoRestored] = useState(false);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Load saved carts on user login/logout
  useEffect(() => {
    if (user?.id) {
      // Clear previous user's data first
      setSavedCarts([]);
      setCurrentCartName(null);
      setCurrentCartId(null);
      setIsModified(false);
      setLastSavedAt(null);
      setAutoSaveEnabled(true);
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      setCart({
        items: [],
        totalItems: 0,
        totalAmount: 0
      });
      
      // Load current user's saved carts
      const storageKey = `savedCarts_${user.id}`;
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        try {
          const parsedCarts = JSON.parse(saved);
          // Verify that all carts belong to current user
          const userCarts = parsedCarts.filter((cart: SavedCart) => cart.userId === user.id);
          setSavedCarts(userCarts);
        } catch (error) {
          logger.error('Failed to parse saved carts', { error: error instanceof Error ? error.message : String(error) });
          setSavedCarts([]);
        }
      }

      // Restore auto-saved cart if available
      const autoSaveKey = `cart_autosave_${user.id}`;
      const autoSaved = localStorage.getItem(autoSaveKey);
      if (autoSaved) {
        try {
          const { items } = JSON.parse(autoSaved);
          if (items && items.length > 0) {
            const totalItems = items.reduce((sum: number, item: CartItem) => sum + item.quantity, 0);
            const totalAmount = items.reduce((sum: number, item: CartItem) => sum + (item.price * item.quantity), 0);
            setCart({ items, totalItems, totalAmount });
            setWasAutoRestored(true);
            logger.info('Cart auto-restored from localStorage', { itemCount: items.length });
          }
        } catch (error) {
          logger.error('Failed to restore auto-saved cart', { error: error instanceof Error ? error.message : String(error) });
        }
      }
    } else {
      // User logged out - clear all cart data
      setSavedCarts([]);
      setCurrentCartName(null);
      setCurrentCartId(null);
      setIsModified(false);
      setLastSavedAt(null);
      setAutoSaveEnabled(true);
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      setCart({
        items: [],
        totalItems: 0,
        totalAmount: 0
      });
    }
  }, [user?.id]);

  // Save carts to localStorage when they change
  useEffect(() => {
    if (user?.id && savedCarts.length > 0) {
      // Filter to ensure we only save carts that belong to current user
      const userCarts = savedCarts.filter(cart => cart.userId === user.id);
      const storageKey = `savedCarts_${user.id}`;
      localStorage.setItem(storageKey, JSON.stringify(userCarts));
    }
  }, [savedCarts, user?.id]);

  // Auto-save current cart items to localStorage on every mutation
  useEffect(() => {
    if (!user?.id) return;
    const autoSaveKey = `cart_autosave_${user.id}`;
    if (cart.items.length > 0) {
      localStorage.setItem(autoSaveKey, JSON.stringify({
        items: cart.items,
        savedAt: new Date().toISOString()
      }));
    } else {
      localStorage.removeItem(autoSaveKey);
    }
  }, [cart.items, user?.id]);

  // Auto-save functionality
  const scheduleAutoSave = useCallback(() => {
    if (!autoSaveEnabled || !currentCartName || !currentCartId || !user?.id) {
      return;
    }

    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Schedule auto-save after 30 seconds of inactivity
    const timer = setTimeout(() => {
      if (currentCartName && currentCartId && isModified) {
        logger.debug('Auto-saving cart');
        
        // Find and update the existing saved cart
        setSavedCarts(prev => {
          const updatedCarts = prev.map(savedCart => {
            if (savedCart.id === currentCartId) {
              return {
                ...savedCart,
                items: cart.items,
                savedAt: new Date().toISOString(),
                totalAmount: cart.totalAmount,
                itemCount: cart.totalItems
              };
            }
            return savedCart;
          });
          return updatedCarts;
        });

        setIsModified(false);
        setLastSavedAt(new Date().toISOString());
        logger.debug('Cart auto-saved successfully');
      }
    }, 30000); // 30 seconds

    autoSaveTimerRef.current = timer;
  }, [autoSaveEnabled, currentCartName, currentCartId, user?.id, isModified, cart]);

  const enableAutoSave = useCallback(() => {
    setAutoSaveEnabled(true);
  }, []);

  const disableAutoSave = useCallback(() => {
    setAutoSaveEnabled(false);
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
  }, []);

  const addToCart = useCallback((items: OrderItem[]): Promise<void> => {
    return new Promise((resolve) => {
      logger.debug('CartContext: addToCart called', { 
        itemsCount: items.length,
        items: items.map(item => ({
          variationId: item.variationId,
          sku: item.sku,
          quantity: item.quantity,
          color: item.color,
          size: item.size
        }))
      });
      
      setCart(prevCart => {
        const newItems = [...prevCart.items];
        
        items.forEach((item, index) => {
          logger.debug(`Processing item ${index + 1}/${items.length}`, {
            variationId: item.variationId,
            quantity: item.quantity
          });
          
          const existingItemIndex = newItems.findIndex(
            cartItem => cartItem.variationId === item.variationId
          );
          
          if (existingItemIndex >= 0) {
            // 既存のアイテムの数量を増やす
            newItems[existingItemIndex] = {
              ...newItems[existingItemIndex],
              quantity: newItems[existingItemIndex].quantity + item.quantity
            };
          } else {
            // 新しいアイテムを追加
            newItems.push({
              ...item,
              id: `${item.variationId}-${Date.now()}-${index}`
            });
          }
        });

        const totalItems = newItems.reduce((sum, item) => sum + item.quantity, 0);
        const totalAmount = newItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

        logger.debug('Cart update complete', {
          newItemsCount: newItems.length,
          totalItems: totalItems,
          totalAmount: totalAmount
        });

        return {
          items: newItems,
          totalItems,
          totalAmount
        };
      });
      
      setIsModified(true);
      scheduleAutoSave();
      
      // Resolve after a small delay to ensure state update
      setTimeout(resolve, 100);
    });
  }, [scheduleAutoSave]);

  const removeFromCart = useCallback((itemId: string) => {
    logger.debug('CartContext: removeFromCart called', { itemId, cartName: currentCartName });
    
    setCart(prevCart => {
      const newItems = prevCart.items.filter(item => item.id !== itemId);
      const totalItems = newItems.reduce((sum, item) => sum + item.quantity, 0);
      const totalAmount = newItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

      logger.debug('New items count after removal', { count: newItems.length });
      
      return {
        items: newItems,
        totalItems,
        totalAmount
      };
    });
    setIsModified(true);
    scheduleAutoSave();
  }, [scheduleAutoSave, currentCartName]);

  const updateQuantity = useCallback((itemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
      return;
    }

    setCart(prevCart => {
      const newItems = prevCart.items.map(item => 
        item.id === itemId ? { ...item, quantity } : item
      );
      const totalItems = newItems.reduce((sum, item) => sum + item.quantity, 0);
      const totalAmount = newItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

      return {
        items: newItems,
        totalItems,
        totalAmount
      };
    });
    setIsModified(true);
    scheduleAutoSave();
  }, [removeFromCart, scheduleAutoSave]);

  const clearAutoRestoredFlag = useCallback(() => {
    setWasAutoRestored(false);
  }, []);

  const clearCart = useCallback(() => {
    logger.debug('CartContext: clearCart called');

    setCart({
      items: [],
      totalItems: 0,
      totalAmount: 0
    });
    setCurrentCartName(null);
    setCurrentCartId(null);
    setIsModified(false);
    setLastSavedAt(null);
  }, []);

  const saveCart = useCallback((name: string) => {
    if (!user?.id || cart.items.length === 0) return;

    const newSavedCart: SavedCart = {
      id: `cart_${Date.now()}`,
      name,
      userId: user.id,
      items: cart.items,
      savedAt: new Date().toISOString(),
      totalAmount: cart.totalAmount,
      itemCount: cart.totalItems
    };

    setSavedCarts(prev => [...prev, newSavedCart]);
    setCurrentCartName(name);
    setCurrentCartId(newSavedCart.id);
    setIsModified(false);
    setLastSavedAt(new Date().toISOString());
    
    logger.info('Cart saved successfully', {
      cartId: newSavedCart.id,
      cartName: name,
      itemCount: cart.totalItems
    });

    logActivity({
      userId: user.id,
      actionType: ACTION_TYPES.CART_SAVED,
      details: {
        cartId: newSavedCart.id,
        cartName: name,
        itemCount: cart.totalItems,
        totalAmount: cart.totalAmount
      }
    });
  }, [cart, user?.id]);

  const loadCart = useCallback((cartId: string) => {
    const savedCart = savedCarts.find(c => c.id === cartId);
    if (!savedCart) {
      logger.warn('Cart not found', { cartId });
      return;
    }

    // Security check: ensure cart belongs to current user
    if (savedCart.userId !== user?.id) {
      logger.critical('Security violation: Attempted to load cart from different user');
      return;
    }

    setCart({
      items: savedCart.items,
      totalItems: savedCart.itemCount,
      totalAmount: savedCart.totalAmount
    });
    setCurrentCartName(savedCart.name);
    setCurrentCartId(savedCart.id);
    setIsModified(false);
    setLastSavedAt(savedCart.savedAt);
  }, [savedCarts, user?.id]);

  const deleteSavedCart = useCallback((cartId: string) => {
    // Security check: ensure cart belongs to current user before deletion
    const cartToDelete = savedCarts.find(c => c.id === cartId);
    if (cartToDelete && cartToDelete.userId !== user?.id) {
      logger.critical('Security violation: Attempted to delete cart from different user');
      return;
    }

    // Check if the deleted cart is currently active
    const isCurrentlyActive = cartToDelete && currentCartName === cartToDelete.name;
    
    setSavedCarts(prev => prev.filter(c => c.id !== cartId));
    
    // If the deleted cart was currently active, clear the current cart name
    if (isCurrentlyActive) {
      setCurrentCartName(null);
      setCurrentCartId(null);
      setIsModified(false);
      setLastSavedAt(null);
    }
    
    // Update localStorage
    if (user?.id) {
      const remaining = savedCarts.filter(c => c.id !== cartId && c.userId === user.id);
      if (remaining.length === 0) {
        localStorage.removeItem(`savedCarts_${user.id}`);
      } else {
        localStorage.setItem(`savedCarts_${user.id}`, JSON.stringify(remaining));
      }
    }
    
    logger.info('Cart deleted', {
      cartId,
      cartName: cartToDelete?.name,
      wasActive: isCurrentlyActive
    });
  }, [savedCarts, user?.id, currentCartName]);

  const startNewCart = useCallback(() => {
    logger.debug('CartContext: startNewCart called');
    clearCart();
  }, [clearCart]);

  return (
    <CartContext.Provider value={{
      cart,
      savedCarts,
      currentCartName,
      currentCartId,
      isModified,
      lastSavedAt,
      autoSaveEnabled,
      wasAutoRestored,
      clearAutoRestoredFlag,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      saveCart,
      loadCart,
      deleteSavedCart,
      startNewCart,
      enableAutoSave,
      disableAutoSave
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};